import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMe, uploadPfp, getStats } from "../lib/api";
import { useTheme } from "../ThemeContext";

const API_BASE = process.env.REACT_APP_API_BASE ?? "/api";

function buildPfpSrc(url, token) {
  if (!url) return null;
  const full = url.startsWith("http")
    ? url
    : `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
  const join = full.includes("?") ? "&" : "?";
  return `${full}${join}t=${encodeURIComponent(token)}`;
}

function fmtHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function rankIndex(username, list) {
  if (!username || !Array.isArray(list)) return -1;
  return list.findIndex((r) => r.username === username);
}

function medalBorder(idx) {
  if (idx === 0) return "border-2 border-yellow-500";
  if (idx === 1) return "border-2 border-gray-400";
  if (idx === 2) return "border-2 border-amber-700";
  return "border";
}

function medalColorHex(idx) {
  if (idx === 0) return "#eab308";
  if (idx === 1) return "#9ca3af";
  if (idx === 2) return "#92400e";
  return null;
}

function StarryBorder({ medalIdx, children }) {
  const color = medalColorHex(medalIdx);
  if (color == null) {
    return (
      <div className={`rounded-2xl p-0 ${medalBorder(medalIdx)}`}>
        <div className="p-4 rounded-2xl bg-white">{children}</div>
      </div>
    );
  }
  return (
    <div
      className={`relative rounded-2xl p-0 ${medalBorder(medalIdx)}`}
      style={{ "--star-color": color }}
    >
      <style>{`
        @keyframes twinkle {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          20% { opacity: 1; }
          60% { opacity: 1; }
          100% { transform: translateY(-6px) scale(1); opacity: 0; }
        }
        .starry { position: absolute; inset: 0; pointer-events: none; }
        .star { position: absolute; width: 4px; height: 4px; background: var(--star-color); border-radius: 9999px; opacity: 0; animation: twinkle 1.8s ease-in-out infinite; }
      `}</style>
      <div className="starry">
        <span
          className="star"
          style={{ top: "-8px", left: "14px", animationDelay: "0s" }}
        />
        <span
          className="star"
          style={{ top: "-10px", right: "22px", animationDelay: "0.35s" }}
        />
        <span
          className="star"
          style={{ bottom: "-9px", left: "18px", animationDelay: "0.7s" }}
        />
        <span
          className="star"
          style={{ bottom: "-11px", right: "12px", animationDelay: "1.05s" }}
        />
        <span
          className="star"
          style={{ top: "50%", left: "-8px", animationDelay: "0.5s" }}
        />
        <span
          className="star"
          style={{ top: "50%", right: "-8px", animationDelay: "1.2s" }}
        />
      </div>
      <div className="p-4 rounded-2xl bg-white relative">{children}</div>
    </div>
  );
}

function StatTile({ title, value, medalIdx }) {
  return (
    <StarryBorder medalIdx={medalIdx}>
      <div className="text-sm text-neutral-500">{title}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </StarryBorder>
  );
}

function List({ title, rows, unit, decimals = 2 }) {
  return (
    <div className="border rounded-2xl p-4">
      <div className="text-sm text-neutral-500">{title}</div>
      <div className="mt-2 space-y-1">
        {rows && rows.length > 0 ? (
          rows.map((r, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="truncate">{r.username}</span>
              <span className="font-mono">
                {unit === "time"
                  ? fmtHMS(r.value)
                  : unit === "int"
                  ? Math.round(r.value)
                  : r.value.toFixed(decimals)}
              </span>
            </div>
          ))
        ) : (
          <div className="text-neutral-400">—</div>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("fw_token") || "";
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    (async () => {
      const tok = localStorage.getItem("fw_token");
      if (!tok) {
        navigate("/");
        return;
      }
      try {
        const [m, st] = await Promise.all([getMe(), getStats()]);
        setMe(m);
        setStats(st);
      } catch {
        navigate("/");
      }
    })();
  }, [navigate]);

  async function onSave() {
    if (!file) return;
    setSaving(true);
    try {
      const updated = await uploadPfp(file);
      setMe(updated);
    } finally {
      setSaving(false);
    }
  }

  const pfpSrc = buildPfpSrc(me?.pfpUrl || "", token);

  const idxSquares = rankIndex(me?.username, stats?.topCuadrados);
  const idxTime = rankIndex(me?.username, stats?.topTiempo);
  const idxMinCps = rankIndex(me?.username, stats?.topMinCps);
  const idxMaxSession = rankIndex(me?.username, stats?.topMaxSession);

  const isLight = theme === "light";
  const isDark = theme === "dark";
  const isBrown = theme === "brown";
  const isPastel = theme === "pastel";
  const isCustom = theme === "custom";

  return (
    <div className="min-h-screen bg-white text-black p-6 flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-mono">perfil</h1>
        <button
          className="rounded-full border px-4 py-2 text-sm"
          onClick={() => navigate("/")}
        >
          Volver
        </button>
      </div>

      <div className="space-y-6">
        <div className="flex items-start gap-3">
          {pfpSrc ? (
            <img
              src={pfpSrc}
              alt=""
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-neutral-200" />
          )}
          <div className="font-mono text-lg leading-tight pt-[2px]">
            {me?.username ?? ""}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <StatTile
            title="tus cuadrados"
            value={stats ? stats.userSquaresTotal : "—"}
            medalIdx={idxSquares}
          />
          <StatTile
            title="tiempo total"
            value={stats ? fmtHMS(stats.userTotalSeconds) : "—"}
            medalIdx={idxTime}
          />
          <StatTile
            title="CPS"
            value={stats ? stats.userCps.toFixed(3) : "—"}
            medalIdx={idxMinCps}
          />
          <StatTile
            title="tu sesión más larga"
            value={stats ? fmtHMS(stats.userMaxSessionSeconds) : "—"}
            medalIdx={idxMaxSession}
          />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <List title="min CPS" rows={stats?.topMinCps} unit="float" decimals={3} />
          <List title="top tiempo" rows={stats?.topTiempo} unit="time" />
          <List title="top cuadrados" rows={stats?.topCuadrados} unit="int" />
          <List
            title="top media cuadrados"
            rows={stats?.topAvgSquares}
            unit="float"
            decimals={2}
          />
          <List title="top media tiempo" rows={stats?.topAvgTime} unit="time" />
          <List
            title="top sesión más larga"
            rows={stats?.topMaxSession}
            unit="time"
          />
        </div>

        <div className="flex flex-col gap-3">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            disabled={!file || saving}
            className={`rounded-full px-6 py-3 ${
              saving
                ? "bg-neutral-300 text-neutral-600"
                : "bg-black text-white"
            }`}
            onClick={onSave}
          >
            {saving ? "Guardando..." : "Subir foto"}
          </button>
        </div>

        <div className="mt-6 border-t pt-4">
          <div className="text-sm text-neutral-500 mb-3">
            Tema de la app en este dispositivo
          </div>
          <div className="inline-flex items-center rounded-full border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={`px-4 py-2 ${
                isLight ? "bg-black text-white" : "bg-transparent"
              }`}
            >
              Claro
            </button>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={`px-4 py-2 ${
                isDark ? "bg-black text-white" : "bg-transparent"
              }`}
            >
              Gris
            </button>
            <button
              type="button"
              onClick={() => setTheme("brown")}
              className={`px-4 py-2 ${
                isBrown ? "bg-black text-white" : "bg-transparent"
              }`}
            >
              Marrón
            </button>
            <button
              type="button"
              onClick={() => setTheme("pastel")}
              className={`px-4 py-2 ${
                isPastel ? "bg-purple-600 text-purple-50" : "bg-transparent"
              }`}
            >
              Pastel
            </button>
            <button
              type="button"
              onClick={() => setTheme("custom")}
              className={`px-4 py-2 ${
                isCustom ? "bg-black text-white" : "bg-transparent"
              }`}
            >
              Custom
            </button>
          </div>
          {isCustom && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => navigate("/theme")}
                className="rounded-full border px-4 py-2 text-xs"
              >
                Personalizar tema
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
